/**
 * DOM and string utilities used across the app.
 *
 * Each function here mirrors a helper that the legacy monolith used
 * inline; consolidating them avoids re-implementation in every component.
 */

/**
 * Debounce a function — the returned function defers invocation until
 * `wait` ms have elapsed since the last call.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @param {number} wait
 * @returns {(...args: Parameters<F>) => void}
 */
export function debounce(fn, wait) {
  let timer = null;
  return function debounced(...args) {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, wait);
  };
}

/**
 * Escape HTML special characters so a string can be safely interpolated
 * into innerHTML. React handles this automatically for children, but
 * this is needed for the rare case where we set HTML strings (e.g. toast
 * titles that include user-supplied strings).
 *
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Generate a short non-cryptographic unique id (used for toasts, modal
 * stack keys, etc.). Not for security purposes.
 *
 * @returns {string}
 */
export function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/**
 * Stable string compare — used by sortable table headers.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function compareStrings(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base' });
}
